import { type UploadProps, message } from 'antd'
import { forwardRef, useImperativeHandle, useState } from 'react'
import { useLingui } from '@lingui/react/macro'
import { cls } from 'tagged-classnames-free'

import type { UploadChangeParam } from 'antd/es/upload'
import type { UploadFile } from 'antd/lib'
import type { IFileResponse } from '@/pages/tad.ai/_components/Upload'

import ImageDisplay from '@/components/ImageDisplay'
import { ImageDisplayStatus } from '@/components/ImageDisplay/type'
import Upload from '@/pages/tad.ai/_components/Upload'
import { useSessionInfo } from '@/contexts/AuthContext/hooks/sessionInfo'
import { useAppUIModal } from '@/contexts/AppUIModalContext'

// 暴露给外部的方法
export interface UploadImageRef {
  abortUpload: () => void
}

export interface IUploadImage extends Omit<UploadProps, 'onChange'> {
  onChange: (status: ImageDisplayStatus, url?: string) => void
  imageUrl?: string
  imageDisplayStatus: ImageDisplayStatus
  onRemove?: () => void
  className?: string
  disabled?: boolean
}

export interface TiltedContainerProps {
  className?: string
  children: React.ReactNode
}

const UploadImage = forwardRef<UploadImageRef, IUploadImage>((props, ref) => {
  const {
    onChange,
    imageUrl,
    imageDisplayStatus,
    onRemove,
    className,
    disabled = false,
  } = props
  const { t } = useLingui()
  const [currentAbortController, setCurrentAbortController] =
    useState<AbortController | null>(null)

  // 支持的图片格式
  const allowedFormats = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp']
  const allowedExtensions = ['.jpg', '.jpeg', '.png', '.webp']
  // 图片大小限制 (20MB)
  const maxSizeMB = 20
  const maxSizeBytes = maxSizeMB * 1024 * 1024

  const { isSignIn } = useSessionInfo()
  const { signUp } = useAppUIModal()
  const shouldShowImageDisplay =
    imageDisplayStatus !== ImageDisplayStatus.EMPTY || !!imageUrl

  // 暴露中断上传的方法给外部
  useImperativeHandle(
    ref,
    () => ({
      abortUpload: () => {
        if (currentAbortController) {
          currentAbortController.abort()
          setCurrentAbortController(null)
          onChange?.(ImageDisplayStatus.EMPTY) // 重置状态
        }
      },
    }),
    [currentAbortController, onChange],
  )

  // 处理上传开始
  const handleUploadStart = (abortController: AbortController) => {
    setCurrentAbortController(abortController)
  }

  // 文件格式和大小验证
  const beforeUpload = (file: File) => {
    const isValidType = allowedFormats.includes(file.type)
    const isValidExtension = allowedExtensions.some((ext) =>
      file.name.toLowerCase().endsWith(ext.toLowerCase()),
    )

    if (!isValidType && !isValidExtension) {
      message.error(t`Only JPG, JPEG, PNG, and WEBP formats are supported!`)
      return false
    }

    // 检查文件大小
    if (file.size > maxSizeBytes) {
      message.error(t`Image size must be less than ${maxSizeMB}MB`)
      return false
    }

    return true
  }

  const handleUploadChange: UploadProps['onChange'] = async (
    info: UploadChangeParam<UploadFile<IFileResponse>>,
  ) => {
    if (info.file.status === 'error') {
      onChange?.(ImageDisplayStatus.ERROR)
      return
    }
    if (info.file.status === 'uploading') {
      onChange?.(ImageDisplayStatus.LOADING)
      return
    }
    if (info.file.status === 'done') {
      const url = info.file.response?.url || ''
      onChange?.(ImageDisplayStatus.LOADED, url)
    }
  }

  return (
    <Upload
      isDragger={false}
      accept='.jpg,.jpeg,.png,.webp'
      onChange={handleUploadChange}
      disabled={disabled}
      showUploadList={false}
      maxCount={1}
      onUploadStart={handleUploadStart}
      beforeUpload={beforeUpload}
    >
      <div
        onClick={(e) => {
          if (!isSignIn) {
            signUp.show({ defaultShowSignUp: true })
            e.stopPropagation()
            e.preventDefault()
          }
        }}
        className={cls`border-f-border flex h-[60px] w-[45px] -rotate-6 cursor-pointer items-center justify-center rounded-lg border border-dashed transition-all duration-300 hover:scale-105 ${className}`}
      >
        {shouldShowImageDisplay ? (
          <ImageDisplay
            className='ml-0 mt-0 size-full'
            status={imageDisplayStatus}
            show={shouldShowImageDisplay}
            imageUrl={imageUrl}
            onRemove={onRemove}
          />
        ) : (
          <span className='i-cus--tad-add-linear text-f-text-quaternary size-5'></span>
        )}
      </div>
    </Upload>
  )
})

UploadImage.displayName = 'UploadImage'

export default UploadImage
